import { useAuthFetch } from "@p0security/frontend-core";
import {
  GetSessionsResponseBody,
  SessionHistoryQueryRow,
} from "@p0security/shared/integrations/sessions/types";
import { message } from "@p0security/ui";
import { ExportOption } from "components/Export";
import { ChangeEvent, useCallback, useState } from "react";

export function useSessionHistory(requestId: string) {
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [search, setSearch] = useState("");
  const [includeResourceSearch, setIncludeResourceSearch] = useState(false);
  const [rows, setRows] = useState<SessionHistoryQueryRow[] | undefined>(
    undefined
  );
  const authFetch = useAuthFetch(setError, setLoading);

  const updateQuantity = useCallback((value: number) => {
    setQuantity(value);
  }, []);

  const updateSearch = useCallback((value: ChangeEvent<HTMLInputElement>) => {
    setSearch(value.target.value);
  }, []);

  const updateIncludeResourceSearch = useCallback((value: boolean) => {
    setIncludeResourceSearch(value);
  }, []);

  const getSession = useCallback(async () => {
    const params = new URLSearchParams();
    params.set("limit", quantity.toString());
    if (search.trim()) {
      params.set("search", search);
      if (includeResourceSearch) {
        params.set("includeResourceSearch", "true");
      }
    }
    const result = await authFetch(
      `sessions/${requestId}?${params.toString()}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!result) {
      setError("Response is empty");
      return;
    }
    if (!result.ok) {
      setError("Response failed");
      return;
    }
    const json: GetSessionsResponseBody = await result.json();
    if (!json.ok) {
      setError(json.error);
      return;
    }
    setRows(json.rows);
  }, [authFetch, quantity, requestId, search, includeResourceSearch]);

  // Async export for all (limit 500)
  const fetchAllRows = useCallback(async (): Promise<
    SessionHistoryQueryRow[]
  > => {
    const params = new URLSearchParams();
    params.set("limit", "500");
    const result = await authFetch(
      `sessions/${requestId}?${params.toString()}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!result) {
      message.error("Error: No response from server");
      return [];
    }
    if (!result.ok) {
      message.error("Error: Failed to fetch all session history");
      return [];
    }
    const json: GetSessionsResponseBody = await result.json();
    if (!json.ok) {
      message.error(json.error || "Unknown error fetching all session history");
      return [];
    }
    return json.rows;
  }, [authFetch, requestId]);

  const toCsv = useCallback(
    async (rows: SessionHistoryQueryRow[]): Promise<string> => {
      if (!rows.length) return "";
      const columns = [
        "requestId",
        "eventId",
        "eventTime",
        "eventName",
        "eventSource",
        "awsRegion",
        "errorCode",
        "ipAddress",
        "userAgent",
        "userIdentity",
        "resources",
        "requestParameters",
      ];
      const escape = (val: unknown) => {
        if (val == null) return "";
        if (typeof val === "object") return `"${JSON.stringify(val)}"`;
        return `"${String(val).replace(/"/g, '""')}"`;
      };
      let result = columns.join(",") + "\n";
      for (const row of rows) {
        result +=
          columns.map((col) => escape((row as any)[col])).join(",") + "\n";
      }
      return result;
    },
    []
  );

  const toJson = useCallback(
    async (rows: SessionHistoryQueryRow[]): Promise<string> => {
      return JSON.stringify(rows, null, 2);
    },
    []
  );

  // Async blob functions for all
  const toCsvAll = useCallback(async () => {
    const allRows = await fetchAllRows();
    return toCsv(allRows);
  }, [fetchAllRows, toCsv]);

  const toJsonAll = useCallback(async () => {
    const allRows = await fetchAllRows();
    return toJson(allRows);
  }, [fetchAllRows, toJson]);

  const exportOptions: Record<string, ExportOption<SessionHistoryQueryRow>> = {
    csv: {
      label: "CSV (current request)",
      blob: toCsv,
      extension: "csv",
    },
    json: {
      label: "JSON (current request)",
      blob: toJson,
      extension: "json",
    },
    csvAll: {
      label: "CSV (all, max 500)",
      blob: async () => toCsvAll(),
      extension: "csv",
    },
    jsonAll: {
      label: "JSON (all, max 500)",
      blob: async () => toJsonAll(),
      extension: "json",
    },
  };

  return {
    rows,
    error,
    loading,
    quantity,
    updateQuantity,
    search,
    updateSearch,
    includeResourceSearch,
    updateIncludeResourceSearch,
    getSession,
    exportOptions,
    requestId,
  };
}
